// The player pages' model (spec/dashboard.md section 11.2): the pinned release's schema as a catalog, the collection ↔
// template rule, the system profile's time fields, the titles of the profile lists' rows, and the guided actions as pure
// edits of a key's value (one reasoned PUT per key results, section 11.4).
import type { EnumDefinition, Json, Parameter, ReleaseSchema, Template } from '../api/types';
import { labelText, type FormatContext } from '../content/format';
import { displayNameFrom, typeDefault } from '../content/schema';
import { formatInstant, type DisplayZone } from '../ui/time';

// ---- ids ---------------------------------------------------------------------------------------------------------------

/** The id of a `player-key` draft: `<env slug>/<player id>/<collection>/<key>` (section 11.4). */
export function playerKeyId(env: string, player: string, collection: string, key: string): string {
  return `${env}/${player}/${collection}/${key}`;
}

export function parsePlayerKeyId(id: string): { env: string; player: string; collection: string; key: string } {
  const [env = '', player = '', collection = '', ...rest] = id.split('/');
  return { env, player, collection, key: rest.join('/') };
}

/**
 * The short form of a player id: the last 12 hex characters. A player id is a v7 uuid whose first characters are a
 * timestamp — players created in the same minute share them — while the tail is random, so the tail tells them apart.
 */
export function shortPlayerId(id: string): string {
  return id.length === 36 ? id.slice(-12) : id;
}

// ---- the system profile's fields -----------------------------------------------------------------------------------------

/** `int` fields of the system profile that hold unix seconds (section 11.2: Balancy's dashboard hard-codes the same editors). */
export const INSTANT_FIELDS = new Set(['startTime', 'shownAt', 'time', 'until', 'c', 'u', 'lastPaymentTime', 'firstLoginTime', 'lastLoginTime', 'dailyRewardCollectTime', 'lastIn', 'lastOut', 'lastTime', 'lastWinTime', 'lastLoseTime', 'levelStartTime', 'completeTime', 'update']);
/** `int` fields that hold a number of seconds (−1 = none on the two "since" counters). */
export const DURATION_FIELDS = new Set(['playTime', 'offlineTime', 'interstitialAdsPeriod', 'timeSinceInstall', 'timeSincePurchase']);

export function timeKind(p: Pick<Parameter, 'name' | 'type'>): 'instant' | 'duration' | null {
  if (p.type !== 'int' && p.type !== 'long') return null;
  if (INSTANT_FIELDS.has(p.name)) return 'instant';
  if (DURATION_FIELDS.has(p.name)) return 'duration';
  return null;
}

/** Keys the dashboard never edits: the bump rule owns `unnySystem` (section 17.1). */
export const READ_ONLY_KEYS = new Set(['unnySystem']);
/** The instance lists of `smartInfo` whose rows offer "Remove this instance". */
export const INSTANCE_LISTS = new Set(['gameEvents', 'gameOffers', 'gameOfferGroups', 'persistentGameEvents']);
export const COOLDOWN_LIST = 'offerCooldowns';
export const INVENTORIES = ['currencies', 'items', 'eventItems'] as const;
export type InventoryName = (typeof INVENTORIES)[number];
export const ITEM_TEMPLATE = 'SmartObjects.Item';

// ---- the release schema as a catalog -----------------------------------------------------------------------------------

interface RawParam {
  name: string;
  type: string;
  template?: string | null;
  enum?: string | null;
  of?: RawParam | null;
  assetKinds?: string[];
  regex?: string | null;
  default?: Json;
  flags?: string[];
  displayName?: string | null;
  description?: string | null;
  group?: string | null;
  isUnique?: boolean;
  isRequired?: boolean;
  usedInName?: boolean;
}

interface RawTemplate {
  name: string;
  kind: string;
  parent?: string | null;
  flags?: string[];
  parameters?: RawParam[];
  unnyId?: string | null;
  displayFormat?: string | null;
}

interface RawEnum {
  name: string;
  isFlags?: boolean;
  values?: { name: string; value: number | string; displayName?: string | null }[];
}

function toParam(p: RawParam, declaredBy: string): Parameter {
  return {
    id: `${declaredBy}.${p.name}`,
    unnyId: null,
    name: p.name,
    displayName: p.displayName ?? null,
    description: p.description ?? null,
    type: p.type as Parameter['type'],
    of: p.of ? toParam({ ...p.of, name: p.of.name ?? '' }, declaredBy) : null,
    template: p.template ?? null,
    enum: p.enum ?? null,
    group: p.group ?? null,
    assetKinds: p.assetKinds ?? [],
    regex: p.regex ?? null,
    default: (p.default ?? null) as Parameter['default'],
    isUnique: !!p.isUnique,
    isRequired: !!p.isRequired,
    usedInName: !!p.usedInName,
    flags: p.flags ?? [],
    legacyFlags: 0,
    declaredBy,
  } as Parameter;
}

/**
 * The templates of a release's `SchemaDto` as the dashboard's `Template` shape. The shipped files list each template's
 * effective parameters (section 14); a parent chain is still walked, so a file that listed own parameters only reads the same.
 */
export function templatesOfSchema(schema: ReleaseSchema | null | undefined): Template[] {
  const raw = (schema?.templates as { templates?: RawTemplate[] } | null | undefined)?.templates ?? [];
  const own = new Map(raw.map((t) => [t.name, t]));
  const effective = new Map<string, Parameter[]>();
  const resolve = (t: RawTemplate, seen: Set<string>): Parameter[] => {
    const known = effective.get(t.name);
    if (known) return known;
    const mine = (t.parameters ?? []).map((p) => toParam(p, t.name));
    let inherited: Parameter[] = [];
    const parent = t.parent ? own.get(t.parent) : undefined;
    if (parent && !seen.has(parent.name)) inherited = resolve(parent, new Set([...seen, parent.name]));
    const names = new Set(mine.map((p) => p.name));
    const all = [...inherited.filter((p) => !names.has(p.name)), ...mine];
    effective.set(t.name, all);
    return all;
  };
  return raw.map((t) => {
    const params = resolve(t, new Set([t.name]));
    const short = t.name.split('.').pop() ?? t.name;
    return {
      id: t.name,
      unnyId: t.unnyId ?? null,
      name: t.name,
      displayName: displayNameFrom(short),
      description: null,
      kind: t.kind,
      parent: t.parent ?? null,
      flags: t.flags ?? [],
      legacyFlags: 0,
      displayFormat: t.displayFormat ?? null,
      order: params.map((p) => p.name),
      parameters: (t.parameters ?? []).map((p) => toParam(p, t.name)),
      effectiveParameters: params,
      selected: null,
      documentCount: 0,
      version: 0,
    } as unknown as Template;
  });
}

export function enumsOfSchema(schema: ReleaseSchema | null | undefined): EnumDefinition[] {
  const raw = (schema?.enums as { enums?: RawEnum[] } | null | undefined)?.enums ?? [];
  return raw.map(
    (e) =>
      ({
        id: e.name,
        unnyId: null,
        name: e.name,
        displayName: displayNameFrom(e.name.split('.').pop() ?? e.name),
        description: null,
        isFlags: !!e.isFlags,
        values: (e.values ?? []).map((v) => ({ name: v.name, value: v.value, displayName: v.displayName ?? null })),
        flags: [],
        legacyFlags: 0,
        version: 0,
      }) as unknown as EnumDefinition,
  );
}

/**
 * The released schema types the tree but carries no editor metadata (display formats, display names, `usedInName`): those
 * come from the release's source branch (section 11.2). Each schema template takes them from the branch template of the same
 * name, parameter by parameter; a template the branch no longer has keeps the schema's shape alone.
 */
export function mergeTemplateMeta(schemaTemplates: Template[], branchTemplates: Template[]): Template[] {
  const byName = new Map(branchTemplates.map((t) => [t.name, t]));
  return schemaTemplates.map((t) => {
    const b = byName.get(t.name);
    if (!b) return t;
    const meta = new Map(b.effectiveParameters.map((p) => [p.name, p]));
    const withMeta = (p: Parameter): Parameter => {
      const m = meta.get(p.name);
      return m ? ({ ...p, displayName: m.displayName ?? p.displayName, description: m.description ?? p.description, usedInName: m.usedInName, group: m.group ?? p.group } as Parameter) : p;
    };
    return { ...t, displayName: b.displayName ?? t.displayName, description: b.description ?? t.description, displayFormat: b.displayFormat ?? t.displayFormat, parameters: t.parameters.map(withMeta), effectiveParameters: t.effectiveParameters.map(withMeta) } as Template;
  });
}

/**
 * The root template of a collection (section 11.2, section 17.2's rule): the `profile_root` (else `profile`) template whose
 * name without its namespace is the collection name; the exact name wins when it exists.
 */
export function collectionTemplate(collection: string, templates: Map<string, Template>): Template | undefined {
  const roots = Array.from(templates.values()).filter((t) => t.kind === 'profile_root' || t.kind === 'profile');
  const last = (name: string) => name.split('.').pop();
  return roots.find((t) => t.name === collection) ?? roots.find((t) => t.kind === 'profile_root' && last(t.name) === collection) ?? roots.find((t) => last(t.name) === collection);
}

/** A section (or a row) at its defaults: nested profile data at their defaults, lists empty, references null. */
export function sectionDefaults(template: Template | undefined, templates: Map<string, Template>, depth = 0): Json {
  if (!template) return null;
  const out: Record<string, Json> = {};
  for (const p of template.effectiveParameters) {
    if (p.type === 'profile_data') out[p.name] = depth < 4 && p.template ? sectionDefaults(templates.get(p.template), templates, depth + 1) : null;
    else if (p.type === 'list') out[p.name] = [];
    else if (p.type === 'document' || p.type === 'asset' || p.type === 'product' || p.type === 'localized_string') out[p.name] = null;
    else if (p.type === 'long') out[p.name] = 0; // the content model keeps longs as digit strings; a profile stores numbers
    else out[p.name] = typeDefault(p);
  }
  return out;
}

// ---- row titles --------------------------------------------------------------------------------------------------------

export interface RowTitleContext {
  format: FormatContext;
  zone: DisplayZone;
  offsets?: { dev?: number; stage?: number };
}

type Obj = Record<string, Json>;
export const obj = (v: unknown): Obj | null => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : null);
const str = (v: unknown) => (typeof v === 'string' ? v : '');
const num = (v: unknown) => (typeof v === 'number' ? String(v) : typeof v === 'string' && v !== '' ? v : '0');
/** an instance id is a uuid: its first eight characters tell instances apart in a title */
const instance = (v: unknown) => (typeof v === 'string' && v.length > 12 ? `${v.slice(0, 8)}…` : str(v));
const join = (...parts: string[]) => parts.filter((p) => p !== '').join(' · ');

/** The name of an enum value through the catalog, else the number. */
export function enumName(enums: Map<string, EnumDefinition>, enumName: string, value: unknown): string {
  const e = enums.get(enumName);
  const hit = e?.values.find((v) => Number(v.value) === Number(value));
  return hit ? (hit.displayName ?? hit.name) : String(value ?? '');
}

/** The title of a row of a profile list (section 11.2's table), from the element template and the row's value. */
export function rowTitle(template: Template | undefined, value: Json, index: number, ctx: RowTitleContext): string {
  const o = obj(value);
  if (!o) return `#${index + 1}`;
  const label = (id: unknown): string => {
    if (typeof id !== 'string' || id === '') return '—';
    const doc = ctx.format.resolve?.(id);
    return doc ? labelText(doc, ctx.format) : id;
  };
  const when = (s: unknown) => (typeof s === 'number' && s > 0 ? formatInstant(s, ctx.zone, { offsets: ctx.offsets }) : 'never');
  const short = template?.name.split('.').pop() ?? '';
  switch (short) {
    case 'EventInfo':
      return join(label(o.gameEvent), instance(o.instanceId));
    case 'OfferInfo':
      return join(label(o.gameOffer), enumName(ctx.format.enums, 'SmartObjects.OfferStatus', o.status), instance(o.instanceId));
    case 'OfferGroupInfo':
      return label(o.gameOfferGroup);
    case 'OfferCooldownInfo':
      return join(label(o.gameOffer ?? o.gameEvent), `until ${when(o.until)}`);
    case 'InventorySlot': {
      const item = obj(o.item);
      return `${label(item?.item)} × ${num(item?.a)}`;
    }
    case 'Purchase': {
      const productId = str(o.productId);
      const productName = productId ? ctx.format.productLabel?.(productId) : undefined;
      return join(productName ?? (productId || '—'), `${num(o.priceUSD)} ${str(o.currency) || 'USD'}`, when(o.time));
    }
    case 'SoftPurchase':
      return join(label(o.item) || str(o.item), num(o.price));
    case 'SegmentInfo':
      return join(label(o.segment), o.isIn ? 'In' : 'Out');
    case 'AbTestInfo':
      return join(label(o.test ?? o.unnyIdTest), label(o.variant ?? o.unnyIdVariant));
    default:
      break;
  }
  const params = template?.effectiveParameters ?? [];
  const docParam = params.find((p) => p.type === 'document' && typeof o[p.name] === 'string' && o[p.name] !== '');
  if (docParam) return label(o[docParam.name]);
  const strParam = params.find((p) => p.type === 'string' && typeof o[p.name] === 'string' && o[p.name] !== '');
  if (strParam) return String(o[strParam.name]);
  return `#${index + 1}`;
}

// ---- guided actions as edits -------------------------------------------------------------------------------------------

/**
 * "Give items" (section 11.2): the minimal edit of the `inventories` key — the slot's item object when the item already has
 * a slot (`a += amount`, `u = now`), else the inventory's slot list with the new v2 slot appended, else the whole inventory
 * when the player never stored it. `path` is relative to the key's value.
 */
export function giveItem(inventories: Json, inventory: InventoryName, itemId: string, amount: number, now: number): { path: string; value: Json } {
  const slot: Json = { acceptableItem: null, item: { item: itemId, a: amount, c: now, u: now } };
  const inv = obj(obj(inventories)?.[inventory]);
  const slots = inv && Array.isArray(inv.slots) ? (inv.slots as Json[]) : null;
  if (!inv || !slots) return { path: inventory, value: { slots: [slot], config: inv?.config ?? null } };
  const i = slots.findIndex((s) => obj(obj(s)?.item)?.item === itemId);
  if (i >= 0) {
    const item = obj(obj(slots[i])?.item) ?? {};
    return { path: `${inventory}.slots[${i}].item`, value: { ...item, item: itemId, a: Number(item.a ?? 0) + amount, u: now } };
  }
  return { path: `${inventory}.slots`, value: [...slots, slot] };
}

/** A list without one row (Remove this instance, Clear this cooldown, Remove). */
export function withoutIndex(list: Json, index: number): Json[] {
  return Array.isArray(list) ? list.filter((_, i) => i !== index) : [];
}

/** The defaults of a `Purchase` row carry the time of the grant (section 11.2). */
export function purchaseDefaults(template: Template | undefined, templates: Map<string, Template>, now: number): Json {
  const d = sectionDefaults(template, templates);
  return obj(d) ? { ...(d as Obj), time: now } : d;
}
